import { useState, useEffect } from "react";

// Local Components
import { LoginLayout } from "../LoginLayout/LoginLayout";
import { RegistrationForm, ConfirmationForm } from "./DemoRegistrationFields";

// Helpers
import {
  validateFormElement,
  validateForm,
  formHasErrors,
  formElementsToBackendRegistrationAttributes,
} from "../../helpers/register";
import type { FormElements } from "../../helpers/register";
import {
  confirmCognitoSignup,
  cognitoResendConfirmationCode,
} from "sj-ui-toolkit/src/integrations/cognito/cognito";
import { getAuthClient, getLoginClient } from "sj-ui-toolkit/src/client";
import { useLoginFlow } from "sj-ui-toolkit/src/hooks";

const DEMO_METADATA = { registrationType: "demo-assessment" };

const INITIAL_FORM_STATE: FormElements = {
  email: { value: "", status: "empty", message: "" },
  password: { value: "", status: "empty", message: "" },
  givenName: { value: "", status: "empty", message: "" },
  familyName: { value: "", status: "empty", message: "" },
  company: { value: "", status: "empty", message: "" },
  jobRole: { value: "", status: "empty", message: "" },
  title: { value: "", status: "empty", message: "" },
};

export function DemoRegistration() {
  const [formElements, setFormElements] = useState<FormElements>(INITIAL_FORM_STATE);
  const [accountCreated, setAccountCreated] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);

  const loginClient = getLoginClient();
  const authClient = getAuthClient();

  const { handlePostLogin } = useLoginFlow();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const passedInAttributes: FormElements = {};

    Object.keys(INITIAL_FORM_STATE).forEach(key => {
      if (key === "password") return;
      const queryValue = params.get(key);
      if (queryValue) {
        passedInAttributes[key] = validateFormElement(key, queryValue);
      }
    });

    setFormElements(prev => ({ ...prev, ...passedInAttributes }));
  }, []);

  const updateAttribute = (name: string, value: string) => {
    setFormElements(prev => ({ ...prev, [name]: validateFormElement(name, value) }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validatedElements = validateForm(formElements);
    setFormElements(validatedElements);

    if (formHasErrors(validatedElements)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const registerResponse = await loginClient.register({
        email: formElements.email.value.toLowerCase(),
        password: formElements.password.value,
        attributes: formElementsToBackendRegistrationAttributes(formElements),
        metadata: DEMO_METADATA,
      });

      if (registerResponse.error) {
        throw new Error(registerResponse.error.message || "Error creating account");
      }

      setAccountCreated(true);
    } catch (error) {
      window.toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error instanceof Error ? error.message : String(error),
        life: 5000,
      });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmationSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsVerifying(true);

    try {
      await confirmCognitoSignup({
        email: formElements.email.value,
        code: confirmationCode,
      });

      const nonceResponse = await loginClient.generateAuthNonce();

      if (nonceResponse.error || !nonceResponse.data?.nonce) {
        console.error("Error generating auth nonce:", nonceResponse.error);
        return;
      }

      const authResponse = await loginClient.passwordAuthenticate({
        username: formElements.email.value,
        password: formElements.password.value,
        nonce: nonceResponse.data.nonce,
      });

      if (authResponse.error || !authResponse.data?.id_token) {
        window.toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: authResponse.error?.message || "Error during authentication",
          life: 5000,
        });
        return;
      }

      const { data, error } = await authClient.exchangeForAccessToken({});
      if (error) {
        window.toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.message || "Error exchanging token",
          life: 5000,
        });
        return;
      }

      if (!data?.access_token) {
        window.toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "No access token returned",
          life: 5000,
        });
        return;
      }

      await handlePostLogin(data.access_token, false, true);
    } catch (error) {
      window.toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error instanceof Error ? error.message : "Error confirming signup",
        life: 5000,
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setIsResendingCode(true);

    try {
      await cognitoResendConfirmationCode({
        email: formElements.email.value,
        metadata: DEMO_METADATA,
      });
    } catch (error) {
      window.toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error instanceof Error ? error.message : "Error resending confirmation code",
        life: 5000,
      });
    } finally {
      setIsResendingCode(false);
    }
  };

  return (
    <LoginLayout
      title="Create an account to access your knowledge assessment demo."
      cardClassName="max-w-2xl"
    >
      <div id="main-content">
        {!accountCreated ? (
          <RegistrationForm
            formElements={formElements}
            onUpdate={updateAttribute}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        ) : (
          <ConfirmationForm
            confirmationCode={confirmationCode}
            onCodeChange={e => setConfirmationCode(e.target.value)}
            onSubmit={handleConfirmationSubmit}
            onResend={handleResendCode}
            isVerifying={isVerifying}
            isResendingCode={isResendingCode}
          />
        )}
      </div>
    </LoginLayout>
  );
}
